import React, {useState, useCallback, useRef} from 'react';
import {
  Box,
  Typography,
  Stack,
  Tabs,
  Tab,
  IconButton,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add,
  Refresh,
  Download,
  SmartToy,
  ArrowBack,
  AudioFile,
} from '@mui/icons-material';
import {useMutation} from 'react-relay-mutation';
import graphql from 'babel-plugin-relay/macro';
import {InspectionList} from './InspectionList';
import {DefectList} from './DefectList';
import {AddDefectDialog} from './AddDefectDialog';
import {AudioSubmissionCard} from './AudioSubmissionCard';
import {AudioPlayerModal} from './AudioPlayerModal';

// GraphQL Mutations
const createInspectionMutation = graphql`
  mutation InspectionPanelCreateInspectionMutation(
    $input: CreateInspectionInput!
  ) {
    createInspection(input: $input) {
      inspection {
        id
        title
        inspectionStatus
        inspectionDate
        propertyAddress
        inspectorName
        defects {
          id
          title
          description
          severity
          defectStatus
          roomType
          defectCategory
          sourceClipIndex
          sourceFramePosition
          aiDetected
          aiConfidence
          evidenceImageURI
          audioStartSeconds
          audioEndSeconds
        }
        audioSubmissions {
          id
          videoURI
          originalFilename
          submissionStatus
          rawTranscript
          durationSeconds
          errorMessage
          createdAt
        }
      }
    }
  }
`;

const createDefectMutation = graphql`
  mutation InspectionPanelCreateDefectMutation($input: CreateDefectInput!) {
    createDefect(input: $input) {
      defect {
        id
        title
        description
        severity
        defectStatus
        roomType
        defectCategory
        sourceClipIndex
        sourceFramePosition
        aiDetected
        aiConfidence
        evidenceImageURI
        audioStartSeconds
        audioEndSeconds
      }
    }
  }
`;

const updateDefectMutation = graphql`
  mutation InspectionPanelUpdateDefectMutation($input: UpdateDefectInput!) {
    updateDefect(input: $input) {
      defect {
        id
        title
        description
        severity
        defectStatus
        roomType
        defectCategory
        sourceClipIndex
        sourceFramePosition
        aiDetected
        aiConfidence
        evidenceImageURI
        audioStartSeconds
        audioEndSeconds
      }
    }
  }
`;

const deleteDefectMutation = graphql`
  mutation InspectionPanelDeleteDefectMutation($input: DeleteDefectInput!) {
    deleteDefect(input: $input) {
      success
      deletedId
    }
  }
`;

const analyzeClipMutation = graphql`
  mutation InspectionPanelAnalyzeClipMutation(
    $input: AnalyzeClipForDefectsInput!
  ) {
    analyzeClipForDefects(input: $input) {
      inspection {
        id
        defects {
          id
          title
          description
          severity
          defectStatus
          roomType
          defectCategory
          sourceClipIndex
          sourceFramePosition
          aiDetected
          aiConfidence
          evidenceImageURI
          audioStartSeconds
          audioEndSeconds
        }
      }
      detectedDefects {
        title
        description
        severity
        roomType
        defectCategory
        framePosition
        confidence
      }
      createdDefects {
        id
        title
      }
    }
  }
`;

const generateReportMutation = graphql`
  mutation InspectionPanelGenerateReportMutation(
    $input: GenerateInspectionReportInput!
  ) {
    generateInspectionReport(input: $input) {
      reportUrl
      inspection {
        id
      }
    }
  }
`;

const submitAudioMutation = graphql`
  mutation InspectionPanelSubmitAudioMutation(
    $input: SubmitAudioForInspectionInput!
  ) {
    submitAudioForInspection(input: $input) {
      audioSubmission {
        id
        videoURI
        originalFilename
        submissionStatus
        rawTranscript
        durationSeconds
        errorMessage
      }
      createdDefects {
        id
        title
        description
        severity
        defectStatus
        roomType
        defectCategory
        aiDetected
        aiConfidence
        audioStartSeconds
        audioEndSeconds
      }
    }
  }
`;

type DefectSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type DefectStatus =
  | 'IDENTIFIED'
  | 'IN_REVIEW'
  | 'ACCEPTED'
  | 'RESOLVED'
  | 'REJECTED';
type InspectionStatus = 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'ARCHIVED';
type SubmissionStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

interface AudioSubmission {
  id: string;
  videoURI?: string | null;
  originalFilename?: string | null;
  submissionStatus: SubmissionStatus;
  rawTranscript?: string | null;
  durationSeconds?: number | null;
  errorMessage?: string | null;
  createdAt?: string | null;
}

interface Defect {
  id: string;
  title: string;
  description?: string | null;
  severity: DefectSeverity;
  defectStatus: DefectStatus;
  roomType?: string | null;
  defectCategory?: string | null;
  sourceClipIndex?: number | null;
  sourceFramePosition?: string | null;
  aiDetected: boolean;
  aiConfidence?: number | null;
  evidenceImageURI?: string | null;
  audioStartSeconds?: number | null;
  audioEndSeconds?: number | null;
}

interface Inspection {
  id: string;
  title: string;
  inspectionStatus: InspectionStatus;
  inspectionDate?: string | null;
  propertyAddress?: string | null;
  inspectorName?: string | null;
  defects?: Defect[];
  audioSubmissions?: AudioSubmission[];
}

interface InspectionPanelProps {
  tourId: string;
  inspections: Inspection[];
  currentClipIndex?: number;
  refetch: () => void;
}

export function InspectionPanel({
  tourId,
  inspections,
  currentClipIndex = 0,
  refetch,
}: InspectionPanelProps) {
  const [tabValue, setTabValue] = useState(0);
  const [
    selectedInspection,
    setSelectedInspection,
  ] = useState<Inspection | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [addDefectOpen, setAddDefectOpen] = useState(false);
  const [editingDefect, setEditingDefect] = useState<Defect | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [audioPlayerOpen, setAudioPlayerOpen] = useState(false);
  const [activeAudioDefect, setActiveAudioDefect] = useState<Defect | null>(
    null
  );
  const [activeAudioVideoUri, setActiveAudioVideoUri] = useState<string | null>(
    null
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [createInspection, {loading: creatingInspection}] = useMutation(
    createInspectionMutation
  );
  const [createDefect, {loading: creatingDefect}] = useMutation(
    createDefectMutation
  );
  const [updateDefect, {loading: updatingDefect}] = useMutation(
    updateDefectMutation
  );
  const [deleteDefect] = useMutation(deleteDefectMutation);
  const [analyzeClip] = useMutation(analyzeClipMutation);
  const [generateReport] = useMutation(generateReportMutation);
  const [submitAudio] = useMutation(submitAudioMutation);

  const handleCreateInspection = useCallback(async () => {
    if (!newTitle.trim()) return;

    try {
      await createInspection({
        variables: {
          input: {
            tourId,
            title: newTitle,
          },
        },
      });
      setNewTitle('');
      setShowCreateForm(false);
      refetch();
    } catch (error) {
      console.error('Failed to create inspection:', error);
    }
  }, [tourId, newTitle, createInspection, refetch]);

  // Handles both create and update based on whether we are editing
  const handleSubmitDefect = useCallback(
    async (data: {
      title: string;
      description: string;
      severity: DefectSeverity;
      defectStatus?: DefectStatus;
      roomType: string;
      defectCategory: string;
      sourceClipIndex?: number;
      sourceFramePosition?: string;
    }) => {
      if (!selectedInspection) return;

      try {
        if (editingDefect) {
          // Update existing defect
          await updateDefect({
            variables: {
              input: {
                defectId: editingDefect.id,
                title: data.title,
                description: data.description || null,
                severity: data.severity,
                defectStatus: data.defectStatus || null,
                roomType: data.roomType || null,
                defectCategory: data.defectCategory || null,
              },
            },
          });
        } else {
          // Create new defect
          await createDefect({
            variables: {
              input: {
                inspectionId: selectedInspection.id,
                title: data.title,
                description: data.description || null,
                severity: data.severity,
                roomType: data.roomType || null,
                defectCategory: data.defectCategory || null,
                sourceClipIndex: data.sourceClipIndex,
                sourceFramePosition: data.sourceFramePosition || null,
              },
            },
          });
        }
        setAddDefectOpen(false);
        setEditingDefect(null);
        refetch();
      } catch (error) {
        console.error('Failed to save defect:', error);
      }
    },
    [selectedInspection, editingDefect, createDefect, updateDefect, refetch]
  );

  const handleDeleteDefect = useCallback(
    async (defectId: string) => {
      try {
        await deleteDefect({
          variables: {input: {defectId}},
        });
        refetch();
      } catch (error) {
        console.error('Failed to delete defect:', error);
      }
    },
    [deleteDefect, refetch]
  );

  const handleAnalyzeClip = useCallback(async () => {
    if (!selectedInspection) return;

    setAnalyzing(true);
    try {
      const result = await analyzeClip({
        variables: {
          input: {
            inspectionId: selectedInspection.id,
            clipIndex: currentClipIndex,
          },
        },
      });
      const createdCount =
        result.analyzeClipForDefects?.createdDefects?.length || 0;
      alert(`AI detected and created ${createdCount} defects`);
      refetch();
    } catch (error) {
      console.error('Failed to analyze clip:', error);
      alert('Failed to analyze clip for defects');
    } finally {
      setAnalyzing(false);
    }
  }, [selectedInspection, currentClipIndex, analyzeClip, refetch]);

  const handleGenerateReport = useCallback(async () => {
    if (!selectedInspection) return;

    setGenerating(true);
    try {
      const result = await generateReport({
        variables: {
          input: {
            inspectionId: selectedInspection.id,
          },
        },
      });
      const reportUrl = result.generateInspectionReport?.reportUrl;
      if (reportUrl) {
        window.open(reportUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      alert('Failed to generate inspection report');
    } finally {
      setGenerating(false);
    }
  }, [selectedInspection, generateReport]);

  const handleAudioFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !selectedInspection) return;

      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setUploadingAudio(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (submitAudio as any)({
          variables: {
            input: {
              inspectionId: selectedInspection.id,
              videoFile: file,
              originalFilename: file.name,
            },
          },
          uploadables: {
            'variables.input.videoFile': file,
          },
        });
        refetch();
      } catch (error) {
        console.error('Failed to submit audio for inspection:', error);
        alert('Failed to upload video for audio analysis');
      } finally {
        setUploadingAudio(false);
      }
    },
    [selectedInspection, submitAudio, refetch]
  );

  const handlePlayAudio = useCallback(
    (defect: Defect) => {
      // Find any completed audio submission with a video URI
      const audioSubs = selectedInspection?.audioSubmissions || [];
      const completedSub = audioSubs.find(
        (s) => s.submissionStatus === 'COMPLETED' && s.videoURI
      );
      const videoUri = completedSub?.videoURI || audioSubs[0]?.videoURI || null;
      setActiveAudioDefect(defect);
      setActiveAudioVideoUri(videoUri);
      setAudioPlayerOpen(true);
    },
    [selectedInspection]
  );

  // View: Create new inspection form
  if (showCreateForm) {
    return (
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={() => setShowCreateForm(false)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">New Inspection</Typography>
        </Box>

        <TextField
          label="Inspection Title"
          required
          fullWidth
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="e.g., Pre-purchase Inspection"
        />

        <Button
          variant="contained"
          onClick={handleCreateInspection}
          disabled={creatingInspection || !newTitle.trim()}
        >
          {creatingInspection ? 'Creating...' : 'Create Inspection'}
        </Button>
      </Stack>
    );
  }

  // View: Selected inspection details
  if (selectedInspection) {
    const audioSubs = selectedInspection.audioSubmissions || [];
    const isDefectLoading = creatingDefect || updatingDefect;

    return (
      <Stack spacing={2} sx={{height: '100%'}}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={() => setSelectedInspection(null)}>
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {selectedInspection.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedInspection.defects?.length || 0} defects
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              analyzing ? <CircularProgress size={16} /> : <SmartToy />
            }
            onClick={handleAnalyzeClip}
            disabled={analyzing}
          >
            {analyzing ? 'Analyzing...' : 'AI Analyze Clip'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setAddDefectOpen(true)}
          >
            Add Defect
          </Button>
        </Stack>

        <Divider />

        <Box flex={1} overflow="auto">
          <DefectList
            defects={selectedInspection.defects || []}
            onEditDefect={(defect) => {
              setEditingDefect(defect);
              setAddDefectOpen(true);
            }}
            onDeleteDefect={handleDeleteDefect}
            onPlayAudio={handlePlayAudio}
          />
        </Box>

        <Divider />

        {/* Audio Analysis Section */}
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Audio Analysis
            </Typography>
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                style={{display: 'none'}}
                onChange={handleAudioFileSelect}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  uploadingAudio ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AudioFile />
                  )
                }
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAudio}
              >
                {uploadingAudio ? 'Uploading...' : 'Upload Video'}
              </Button>
            </Box>
          </Box>

          {audioSubs.length === 0 && !uploadingAudio && (
            <Typography variant="caption" color="text.secondary">
              Upload a walkthrough video to extract snags via audio analysis.
            </Typography>
          )}

          <Stack spacing={1}>
            {audioSubs.map((sub) => (
              <AudioSubmissionCard key={sub.id} submission={sub} />
            ))}
          </Stack>
        </Box>

        <Divider />

        <Button
          variant="contained"
          startIcon={generating ? <CircularProgress size={16} /> : <Download />}
          onClick={handleGenerateReport}
          disabled={
            generating || (selectedInspection.defects?.length || 0) === 0
          }
        >
          {generating ? 'Generating...' : 'Generate PDF Report'}
        </Button>

        <AddDefectDialog
          open={addDefectOpen}
          onClose={() => {
            setAddDefectOpen(false);
            setEditingDefect(null);
          }}
          onSubmit={handleSubmitDefect}
          initialData={editingDefect || undefined}
          isEditing={!!editingDefect}
          loading={isDefectLoading}
          currentClipIndex={currentClipIndex}
        />

        <AudioPlayerModal
          open={audioPlayerOpen}
          onClose={() => {
            setAudioPlayerOpen(false);
            setActiveAudioDefect(null);
            setActiveAudioVideoUri(null);
          }}
          defect={activeAudioDefect}
          videoUri={activeAudioVideoUri}
        />
      </Stack>
    );
  }

  // View: Inspections list
  return (
    <Stack spacing={2} sx={{height: '100%'}}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Inspections</Typography>
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={refetch}>
            <Refresh />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={() => setShowCreateForm(true)}
          >
            <Add />
          </IconButton>
        </Stack>
      </Box>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="fullWidth"
      >
        <Tab label="Active" />
        <Tab label="Completed" />
      </Tabs>

      <Box flex={1} overflow="auto">
        <InspectionList
          inspections={inspections.filter((i) =>
            tabValue === 0
              ? i.inspectionStatus !== 'COMPLETED' &&
                i.inspectionStatus !== 'ARCHIVED'
              : i.inspectionStatus === 'COMPLETED' ||
                i.inspectionStatus === 'ARCHIVED'
          )}
          selectedId={selectedInspection?.id}
          onSelect={setSelectedInspection}
        />
      </Box>

      {inspections.length === 0 && (
        <Alert severity="info">
          Create an inspection to start documenting property defects and
          generate professional snaglist reports.
        </Alert>
      )}
    </Stack>
  );
}
